import { useRef } from "react"
import { BackgroundCanvas, CSS, Counter, Reveal } from "./CampusBrochureShared"

// Campus ambassador brochure: fixed ₹5,000/month stipend for five ambassadors, three-month pilot.
// `campus` pre-selects the college on the application form.

const EXTRA_CSS = `
.amb-stipend{display:grid;grid-template-columns:1.1fr 1fr;gap:20px;align-items:stretch}
.amb-stipend-card{border-radius:24px;padding:48px 40px;border:1.5px solid rgba(255,230,0,.22);background:linear-gradient(135deg,rgba(255,200,0,.1),rgba(255,140,0,.04));position:relative;overflow:hidden}
.amb-stipend-amt{font-size:clamp(3.4rem,7vw,5.5rem);font-weight:900;letter-spacing:-.05em;line-height:1;color:#ffe600}
.amb-stipend-per{font-size:1.1rem;font-weight:700;margin:12px 0 18px}
.amb-stipend-note{font-size:.95rem;color:rgba(255,255,255,.55);line-height:1.7}
.amb-stipend-side{border-radius:24px;padding:40px 36px;border:1.5px solid rgba(167,139,250,.22);background:linear-gradient(135deg,rgba(85,34,125,.4),rgba(120,50,185,.15))}
.amb-stipend-side h3{font-size:1.05rem;font-weight:800;margin-bottom:14px}
.amb-stipend-side p{font-size:.92rem;color:rgba(255,255,255,.55);line-height:1.7}
.amb-stipend-side p+p{margin-top:12px}
.amb-resp-list{display:grid;grid-template-columns:repeat(2,1fr);gap:14px}
.amb-resp{display:flex;gap:16px;align-items:flex-start;border-radius:18px;padding:22px 24px;background:rgba(255,255,255,.04);border:1.5px solid rgba(255,255,255,.08);transition:border-color .3s,background .3s}
.amb-resp:hover{border-color:rgba(255,230,0,.3);background:rgba(255,255,255,.06)}
.amb-resp-e{font-size:1.6rem;line-height:1}
.amb-resp-t{font-size:.98rem;font-weight:700;line-height:1.5}
@media(max-width:860px){.amb-stipend,.amb-resp-list{grid-template-columns:1fr}}
@media(max-width:680px){.amb-stipend-card,.amb-stipend-side{padding:32px 24px}.hero{padding:110px 20px 90px}}
`

const responsibilities = (college) => [
    { e: "✍️", t: "Create 4–6 useful pieces of campus content" },
    { e: "🎪", t: "Run one approved campus activation" },
    { e: "🤝", t: "Introduce Blox in student communities, with the group's permission" },
    { e: "📲", t: "Help interested students understand and join the app" },
    { e: "🌱", t: `Keep the ${college} community on Blox active and welcoming` },
    { e: "🗣️", t: "Collect student feedback and send a weekly update" },
    { e: "📞", t: "Join one short team call each week" },
    { e: "🛡️", t: "Follow Blox's brand, safety, privacy and no-spam guidelines" },
]

const perks = (college) => [
    { e: "🏅", t: '"Founding Member" Badge', s: `Permanent on your Blox profile — visible to everyone at ${college} and beyond.` },
    { e: "🧭", t: "Shape the Product", s: "Your weekly feedback goes straight to the people building Blox." },
    { e: "💼", t: "Startup Experience", s: "Real work at a live product — campus launches, content and community building." },
    { e: "🚀", t: "First Access to Everything", s: "New features, events and Blox opportunities reach founding ambassadors first." },
]

export default function CampusStipendBrochure({ college, eligibility, campus }) {
    const particles = useRef(
        Array.from({ length: 30 }, (_, i) => ({
            id: i,
            left: Math.random() * 100,
            size: 1.5 + Math.random() * 3,
            delay: Math.random() * 18,
            dur: 12 + Math.random() * 14,
            color: Math.random() > 0.45 ? "#ffe600" : "#a78bfa",
        }))
    ).current

    return (
        <div className="cb">
            <style>{CSS + EXTRA_CSS}</style>
            <BackgroundCanvas />

            {/* ── HERO ── */}
            <section className="hero">
                <div className="aurora">
                    <div className="aurora-band" style={{ width: "60vw", height: "50vh", top: "-10%", left: "-5%", background: "rgba(85,34,125,.35)", animationDuration: "12s", animationDelay: "0s" }} />
                    <div className="aurora-band" style={{ width: "50vw", height: "45vh", top: "5%", right: "-5%", background: "rgba(255,180,0,.12)", animationDuration: "15s", animationDelay: "-5s" }} />
                    <div className="aurora-band" style={{ width: "40vw", height: "40vh", bottom: "-5%", left: "20%", background: "rgba(60,20,120,.3)", animationDuration: "18s", animationDelay: "-8s" }} />
                </div>
                <div className="ptcl">
                    {particles.map(p => (
                        <div key={p.id} className="p" style={{
                            left: `${p.left}%`, bottom: -8,
                            width: p.size, height: p.size,
                            background: p.color,
                            animationDelay: `${p.delay}s`,
                            animationDuration: `${p.dur}s`,
                        }} />
                    ))}
                </div>

                <div className="chip">⚡ ₹5,000 fixed monthly stipend · 5 positions · 3-month pilot</div>
                <h1>
                    <span className="dim">Become a Founding</span>
                    <em>Blox Campus Ambassador</em>
                </h1>
                <p className="hero-sub">
                    We're selecting five students to introduce Blox at {college}, create meaningful
                    campus experiences and help shape the product.
                </p>
                <div className="hero-btns">
                    <a href={`/partner-registration?campus=${campus}`} className="btn-primary">Apply now →</a>
                    <a href="#stipend" className="btn-ghost">See the stipend</a>
                </div>
                <div className="scroll-hint">
                    <div className="scroll-line" />
                </div>
            </section>

            {/* ── SPOTS BAR ── */}
            <div className="spots-bar">
                <strong>5 positions</strong> · {eligibility} · {college} · Receive ₹5,000 per month
            </div>

            {/* ── STATS ── */}
            <Reveal>
                <div className="stats-row">
                    {[
                        { n: 5000, pre: "₹", suf: "", l: "Per Month, Fixed" },
                        { n: 5, pre: "", suf: "", l: "Positions" },
                        { n: 3, pre: "", suf: " mo", l: "Pilot Programme" },
                        { n: 0, pre: "", suf: "", l: "Referral Quotas" },
                    ].map((s, i) => (
                        <div key={i} className="stat-cell">
                            <div className="stat-val"><Counter to={s.n} prefix={s.pre} suffix={s.suf} /></div>
                            <div className="stat-lbl">{s.l}</div>
                        </div>
                    ))}
                </div>
            </Reveal>

            <div className="div" />

            {/* ── STIPEND ── */}
            <section className="sec" id="stipend">
                <div className="sec-glow" style={{ width: "500px", height: "500px", top: "-100px", right: "-100px", background: "rgba(255,200,0,.07)" }} />
                <div className="sec-inner" style={{ position: "relative", zIndex: 1 }}>
                    <Reveal>
                        <div className="sec-eyebrow">The Stipend</div>
                        <div className="sec-title">₹5,000 every month.<br />No quota.</div>
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="amb-stipend">
                            <div className="amb-stipend-card">
                                <div className="eng-glow" style={{ background: "#ffe600" }} />
                                <div className="amb-stipend-amt">₹5,000</div>
                                <div className="amb-stipend-per">Fixed monthly stipend</div>
                                <div className="amb-stipend-note">
                                    Every selected Blox Campus Ambassador receives a fixed stipend of ₹5,000
                                    per month during the programme.
                                </div>
                            </div>
                            <div className="amb-stipend-side">
                                <h3>Paid for the work, not for sign-ups</h3>
                                <p>The stipend is based on completing the agreed ambassador responsibilities — not on hitting a referral quota.</p>
                                <p>No mass messages, no chasing numbers. We want useful content, good campus events and honest feedback.</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <div className="div" />

            {/* ── RESPONSIBILITIES ── */}
            <section className="sec" id="responsibilities">
                <div className="sec-glow" style={{ width: "600px", height: "600px", top: "-200px", left: "-150px", background: "rgba(85,34,125,.18)" }} />
                <div className="sec-inner" style={{ position: "relative", zIndex: 1 }}>
                    <Reveal>
                        <div className="sec-eyebrow">Monthly Responsibilities</div>
                        <div className="sec-title">What you'll do<br />each month.</div>
                    </Reveal>
                    <div className="amb-resp-list">
                        {responsibilities(college).map((r, i) => (
                            <Reveal key={i} delay={i * 50}>
                                <div className="amb-resp">
                                    <span className="amb-resp-e">{r.e}</span>
                                    <div className="amb-resp-t">{r.t}</div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <div className="div" />

            {/* ── BEYOND THE STIPEND ── */}
            <section className="sec">
                <div className="sec-glow" style={{ width: "500px", height: "400px", top: 0, right: "-100px", background: "rgba(255,200,0,.06)" }} />
                <div className="sec-inner" style={{ position: "relative", zIndex: 1 }}>
                    <Reveal>
                        <div className="sec-eyebrow">Beyond the Stipend</div>
                        <div className="sec-title">This goes on your<br />profile. Permanently.</div>
                    </Reveal>
                    <div className="perk-grid">
                        {perks(college).map((p, i) => (
                            <Reveal key={i} delay={i * 70}>
                                <div className="perk">
                                    <span className="perk-e">{p.e}</span>
                                    <div className="perk-t">{p.t}</div>
                                    <div className="perk-s">{p.s}</div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FINAL CTA ── */}
            <section className="cta-sec">
                <div className="cta-bg" />
                <Reveal>
                    <div className="live-dot"><div className="dot" />Applications open now</div>
                    <h2><em>5 founding</em> ambassadors.<br />Be one.</h2>
                    <p>Reviewed within 48 hours. Onboarding call within 5 days.<br />Receive ₹5,000 per month for the three-month pilot.</p>
                    <a href={`/partner-registration?campus=${campus}`} className="btn-primary" style={{ fontSize: "1.1rem", padding: "20px 52px" }}>
                        Apply to be a Founding Ambassador
                    </a>
                </Reveal>
            </section>
        </div>
    )
}
